import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { injectIntl } from 'react-intl'
import { FormattedDate } from 'components/intl'
import { get } from 'lodash'
import moment from 'moment'
import { compose, pure } from 'recompose'

import { Modal } from 'containers/misc'
import { ParticipantSettings } from 'containers/forms/participants'
import { ContactRemove } from 'containers/forms/contacts'
import { EmailSend } from 'containers/forms/emails'
import { Module, BadgesLevels, FormData, Status, Button } from 'components/misc'
import { Title } from 'components/layout'
import activeAward from 'helpers/activeAward'
import { ROLES, PERSON_STATES, REMOVE_CONTACT_TYPES } from 'constants/enums'
import { getActiveRole } from 'utils/token'
import { hasGoldAward } from 'redux/selectors/awards'

import { filterByRole } from 'redux/selectors/misc'
import { Checkbox } from '../../../fields'
import { permissions } from '../../../../helpers/decorators'

const enhance = compose(
  connect(({ auth }) => ({
    activeOrganization: auth.organization
  })),
  injectIntl,
  pure,
  permissions()
)

class ParticipantOverview extends PureComponent {
  static propTypes = {
    data: PropTypes.object.isRequired,
    activeOrganization: PropTypes.object.isRequired,
    id: PropTypes.number,
    participantDeletionTypes: PropTypes.array,
    deleteParticipant: PropTypes.func.isRequired,
    sendConfirmationEmail: PropTypes.func.isRequired,
    sendRecoveryPassword: PropTypes.func.isRequired,
    inviteParticipantToNextLevel: PropTypes.func.isRequired,
    modal: PropTypes.func.isRequired,
    inviteLoading: PropTypes.bool,
    intl: PropTypes.object,
    permissions: PropTypes.object
  }

  getPersonState = data => {
    const isActivating = get(data, 'isSetToActivate')

    if (isActivating) {
      return {
        value: PERSON_STATES.activating.id,
        translationText: this.props.intl.formatMessage({
          id: 'label.person_status.activating',
          defaultMessage: 'Activation In Progress'
        })
      }
    }

    return get(data, 'personState')
  }

  handleDialogClose = () => this.props.modal(null)

  render() {
    const {
      data = {},
      id,
      participantDeletionTypes,
      activeOrganization,
      inviteLoading
    } = this.props

    const activeIndex = activeAward(data)
    const role = getActiveRole(activeOrganization)

    const actions = [
      {
        id: 1,
        label: "Change Participant's status",
        intlKey: 'participant.change_participant_status',
        onClick: () => this.props.modal('participant-account-settings')
      },
      {
        id: 2,
        label: 'Send confirmation email again',
        intlKey: 'participant.send_confirmation_email_again',
        onClick: () => this.props.modal('participant-confirmation-email')
      },
      {
        id: 3,
        label: 'Recover password',
        intlKey: 'participant.recover_password',
        onClick: () => this.props.modal('participant-recover-password')
      },
      {
        id: 4,
        label: 'Send test email',
        intlKey: 'contact.send_test_email',
        onClick: () => this.props.modal('send-test-email'),
        roles: [ROLES.IF_STAFF_SUPER.u]
      },
      {
        id: 5,
        label: 'Next level invitation',
        intlKey: 'contact.next_level_invitation',
        onClick: () => this.props.modal('next-level-invitation')
      },
      {
        id: 6,
        label: 'Delete account',
        intlKey: 'participant.delete_account',
        onClick: () => this.props.modal('participant-account-remove')
      }
    ]

    const actionsByRole = filterByRole(actions, role)

    const closeButton = (
      <div className="col-xs-6">
        <Button
          type="button"
          onClick={this.handleDialogClose}
          secondary
          intlKey="button.close"
        >
          Close
        </Button>
      </div>
    )

    const hasGold = hasGoldAward(get(data, 'awards', []))

    return (
      <div>
        <Module
          title="Overview"
          intlKey="moduleTitle.overview"
          primary
          grid
          actionDropdown="Account Settings"
          actionDropdownActions={actionsByRole}
          intlKeyAction="button.account_settings"
          iconAction="settings"
        >
          <div className="row">
            <div className="col-xs-2">
              <FormData label="Age" intlKey="label.age">
                {get(data, 'birthDate') &&
                  moment().diff(data.birthDate, 'years')}
              </FormData>
            </div>
            <div className="col-xs-4">
              <FormData label="Organisation" intlKey="label.organization">
                {get(data, `awards[${activeIndex}].organization.name`)}
              </FormData>
            </div>
            <div className="col-xs-3">
              <FormData
                label="Registration Date"
                intlKey="label.registration_date"
              >
                {get(data, `awards[${activeIndex}].createdAt`) && (
                  <FormattedDate value={data.awards[activeIndex].createdAt} />
                )}
              </FormData>
            </div>
            <div className="col-xs-3">
              <FormData label="State" intlKey="label.state">
                <Status state={this.getPersonState(data)} />
              </FormData>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-3">
              <FormData label="Level" intlKey="label.level">
                {get(data, 'awards') && <BadgesLevels levels={data.awards} />}
              </FormData>

            </div>
             <div className="col-xs-6">
              <div style={{ margin: '-18px 0px 0px 20px', paddingLeft: '20px' }}>
                <Checkbox
                    label="Allow adding logs after 25 years of age"
                    intlKey="label.allow_log_after_25_yoa"
                    name="isAddActivityLogAllowed"
                    justify
                />
              </div>
            </div>
          </div>
        </Module>
        <Modal
          id="participant-account-settings"
          compact
          action="Select"
          intlKeyAction="modal.select"
          customActions
        >
          <ParticipantSettings {...this.props} />
        </Modal>
        <Modal id="participant-account-remove" customActions enableOverflow>
          <ContactRemove
            id={id}
            onDelete={this.props.deleteParticipant}
            reasonsList={participantDeletionTypes}
            awards={get(data, 'awards')}
            contactRemoveType={REMOVE_CONTACT_TYPES.PARTICIPANT.value}
          />
        </Modal>
        <Modal id="participant-recover-password" customActions>
          <div className="row">
            <div className="col-xs-12">
              <Title
                h2
                intlKey="participant.send_password_recovery_email"
                className="text--center"
              >
                Send password recovery email?
              </Title>
            </div>
            {closeButton}
            <div className="col-xs-6 text--right">
              <Button
                type="button"
                intlKey="button.confirm"
                onClick={() =>
                  this.props.sendRecoveryPassword(get(data, 'email'))
                }
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
        <Modal id="participant-confirmation-email" customActions>
          <div className="row">
            <div className="col-xs-12">
              <Title
                h2
                intlKey="participant.send_confirmation_email_again_question"
                className="text--center"
              >
                Send confirmation email again?
              </Title>
            </div>
            {closeButton}
            <div className="col-xs-6 text--right">
              <Button
                type="button"
                intlKey="button.confirm"
                onClick={() =>
                  this.props.sendConfirmationEmail(get(data, 'id'))
                }
              >
                Confirm
              </Button>
            </div>
          </div>
        </Modal>
        <Modal id="next-level-invitation" customActions>
          <div className="row">
            <div className="col-xs-12">
              {hasGold ? (
                <Title
                  h2
                  intlKey="participant.participant_already_has_gold_award"
                  className="text--center"
                >
                  Participant already has Gold award
                </Title>
              ) : (
                <Title
                  h2
                  intlKey="participant.invite_participant_to_next_level"
                  className="text--center"
                >
                  Invite participant to next level?
                </Title>
              )}
            </div>
            {closeButton}

            {!hasGold && (
              <div className="col-xs-6 text--right">
                <Button
                  type="button"
                  intlKey="button.confirm"
                  isLoading={inviteLoading}
                  disabled={hasGold}
                  onClick={() =>
                    this.props.inviteParticipantToNextLevel(
                      get(data, `awards[${activeIndex}].id`)
                    )
                  }
                >
                  Confirm
                </Button>
              </div>
            )}
          </div>
        </Modal>
        <Modal id="send-test-email" customActions>
          <EmailSend
            onCancel={this.handleDialogClose}
            personId={get(data, 'id')}
          />
        </Modal>
      </div>
    )
  }
}

export default enhance(ParticipantOverview)
